import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Company information for SEO
const companyInfo = {
  name: 'C3 Contracting',
  legalName: 'C3 Contracting, LLC',
  tagline: 'Building with a Deeper Purpose',
  description: 'C3 Contracting is a faith-based design-build general contractor serving Concord, Davidson, Mooresville, Kannapolis, and Lake Norman NC. Specializing in custom home building, whole-home renovations, additions, and remodeling.',
  phone: '980-680-6703',
  phoneFormatted: '(980) 680-6703',
  email: 'info@c3-contracting.com',
  address: {
    street: '6355 Alexander Road',
    city: 'Concord',
    state: 'NC',
    zip: '28027',
    country: 'US'
  },
  geo: {
    lat: 35.4088,
    lng: -80.5795
  },
  hours: 'Mo-Fr 08:00-17:00',
  foundingYear: 2022,
  url: 'https://c3-contracting.com',
  logo: 'https://c3-contracting.com/images/c3-logo.png',
  socialProfiles: [
    'https://www.facebook.com/c3contracting',
    'https://www.instagram.com/c3contracting'
  ],
  serviceAreas: ['Concord', 'Davidson', 'Mooresville', 'Kannapolis', 'Lake Norman', 'Charlotte', 'Huntersville', 'Cornelius']
};

// Page-specific SEO data
const pageSEO = {
  '/': {
    title: 'C3 Contracting | Custom Home Builder & Design-Build Contractor | Concord, Lake Norman NC',
    description: 'C3 Contracting is a premier design-build general contractor in Concord & Lake Norman NC. Specializing in custom homes, whole-home renovations, additions & remodeling. Faith-based values, exceptional craftsmanship. Call (980) 680-6703.',
    keywords: 'home builder Concord NC, custom homes Lake Norman, general contractor Davidson, design-build Mooresville, home additions Kannapolis, home remodeling Charlotte NC'
  },
  '/services': {
    title: 'Our Services | Custom Home Building, Renovations & Remodeling | C3 Contracting',
    description: 'Comprehensive design-build services including custom home construction, whole-home transformations, home additions, and remodeling in Concord, Davidson, Mooresville, Kannapolis & Lake Norman NC. Interior design included.',
    keywords: 'custom home builder, home renovation contractor, home additions, kitchen remodeling, bathroom remodeling, design-build services, Lake Norman contractor'
  },
  '/portfolio': {
    title: 'Project Portfolio | Custom Homes & Renovations Gallery | C3 Contracting',
    description: 'View our portfolio of completed custom homes, whole-home renovations, additions, and remodeling projects in the Lake Norman and Charlotte NC area. See our exceptional craftsmanship.',
    keywords: 'custom home portfolio, renovation projects, home remodel gallery, Lake Norman home builder projects, Charlotte contractor portfolio'
  },
  '/about': {
    title: 'About C3 Contracting | Faith-Based Design-Build Contractor | Our Story',
    description: 'Learn about C3 Contracting, a faith-based design-build company founded by Daniel Wayne. Building with a deeper purpose since 2022. Serving Concord, Davidson, Mooresville & Lake Norman NC.',
    keywords: 'about C3 Contracting, Daniel Wayne contractor, faith-based builder, design-build company, Lake Norman home builder'
  },
  '/contact': {
    title: 'Contact Us | Get a Free Consultation | C3 Contracting',
    description: 'Contact C3 Contracting for your custom home, renovation, or remodeling project. Free consultations available. Serving Concord, Davidson, Mooresville, Kannapolis & Lake Norman NC. Call (980) 680-6703.',
    keywords: 'contact home builder, free consultation, Lake Norman contractor contact, Concord NC builder, get quote home renovation'
  },
  '/consultation': {
    title: 'Request a Consultation | Start Your Project | C3 Contracting',
    description: 'Request a free consultation for your custom home build, renovation, or remodeling project. Our design-build team serves Concord, Davidson, Mooresville, Kannapolis & Lake Norman NC.',
    keywords: 'free home consultation, project consultation, custom home quote, renovation estimate, Lake Norman builder consultation'
  },
  '/estimator': {
    title: 'Project Estimator | Get Instant Cost Estimates | C3 Contracting',
    description: 'Use our interactive project estimator to get instant conceptual cost estimates for your custom home, renovation, addition, or remodeling project. No commitment required.',
    keywords: 'home construction cost estimator, renovation cost calculator, home addition estimate, remodeling budget tool'
  },
  '/careers': {
    title: 'Join Our Team | Construction Careers | C3 Contracting',
    description: 'Join C3 Contracting and build your career with a faith-based design-build company. View open positions in construction, project management, and more in the Lake Norman NC area.',
    keywords: 'construction jobs Lake Norman, contractor careers Charlotte, home builder jobs, construction employment NC'
  },
  '/vendor-application': {
    title: 'Vendor Application | Become a Subcontractor | C3 Contracting',
    description: 'Apply to become a vendor or subcontractor with C3 Contracting. Join our network of trusted trade professionals serving the Lake Norman and Charlotte NC area.',
    keywords: 'subcontractor application, vendor registration, construction subcontractor, trade contractor Lake Norman'
  },
  '/jobtread': {
    title: 'Customer Portal | JobTread Project Management | C3 Contracting',
    description: 'Access your C3 Contracting customer portal powered by JobTread. Track your project progress, view schedules, communicate with our team, and manage selections.',
    keywords: 'customer portal, project management, construction tracking, JobTread portal'
  },
  '/employment-application': {
    title: 'Employment Application | Join Our Team | C3 Contracting',
    description: 'Apply for a position at C3 Contracting. Join our team of skilled professionals and build your career with a faith-based design-build contractor in Lake Norman NC.',
    keywords: 'construction job application, contractor careers, home builder employment, Lake Norman construction jobs'
  },
  '/privacy-policy': {
    title: 'Privacy Policy | C3 Contracting',
    description: 'C3 Contracting privacy policy. Learn how we collect, use, and protect your personal information when you use our website and services.',
    keywords: 'privacy policy, data protection, C3 Contracting privacy'
  },
  '/terms-of-service': {
    title: 'Terms of Service | C3 Contracting',
    description: 'C3 Contracting terms of service. Read our terms and conditions for using our website and construction services.',
    keywords: 'terms of service, terms and conditions, C3 Contracting terms'
  }
};

// Generate LocalBusiness Schema
const generateLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'HomeAndConstructionBusiness',
  '@id': `${companyInfo.url}/#organization`,
  name: companyInfo.name,
  legalName: companyInfo.legalName,
  description: companyInfo.description,
  url: companyInfo.url,
  logo: companyInfo.logo,
  image: companyInfo.logo,
  telephone: companyInfo.phone,
  email: companyInfo.email,
  foundingDate: companyInfo.foundingYear.toString(),
  address: {
    '@type': 'PostalAddress',
    streetAddress: companyInfo.address.street,
    addressLocality: companyInfo.address.city,
    addressRegion: companyInfo.address.state,
    postalCode: companyInfo.address.zip,
    addressCountry: companyInfo.address.country
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: companyInfo.geo.lat,
    longitude: companyInfo.geo.lng
  },
  openingHours: companyInfo.hours,
  areaServed: companyInfo.serviceAreas.map(area => ({
    '@type': 'City',
    name: area,
    '@id': `https://en.wikipedia.org/wiki/${area.replace(' ', '_')},_North_Carolina`
  })),
  sameAs: companyInfo.socialProfiles,
  priceRange: '$$$$',
  paymentAccepted: 'Cash, Check, Credit Card',
  currenciesAccepted: 'USD',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Construction Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Custom Home Building',
          description: 'Design and build custom homes tailored to your lifestyle and preferences.'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Whole-Home Renovations',
          description: 'Complete home transformations including structural modifications and modern updates.'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Home Additions',
          description: 'Room additions, second-story additions, and seamless expansions to your existing home.'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Home Remodeling',
          description: 'Kitchen remodeling, bathroom renovations, and room-specific transformations.'
        }
      }
    ]
  }
});

// Generate WebSite Schema
const generateWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${companyInfo.url}/#website`,
  url: companyInfo.url,
  name: companyInfo.name,
  description: companyInfo.description,
  publisher: {
    '@id': `${companyInfo.url}/#organization`
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${companyInfo.url}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string'
  }
});

// Generate BreadcrumbList Schema
const generateBreadcrumbSchema = (pathname) => {
  const paths = pathname.split('/').filter(Boolean);
  const breadcrumbs = [
    { name: 'Home', url: companyInfo.url }
  ];
  
  let currentPath = '';
  paths.forEach((path, index) => {
    currentPath += `/${path}`;
    const name = path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, ' ');
    breadcrumbs.push({
      name,
      url: `${companyInfo.url}${currentPath}`
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
};

const SEO = ({ 
  title, 
  description, 
  keywords,
  image,
  type = 'website',
  noindex = false 
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  
  // Get page-specific SEO or use defaults
  const pageData = pageSEO[pathname] || {};
  const finalTitle = title || pageData.title || `${companyInfo.name} | ${companyInfo.tagline}`;
  const finalDescription = description || pageData.description || companyInfo.description;
  const finalKeywords = keywords || pageData.keywords || '';
  const finalImage = image || companyInfo.logo;
  const canonicalUrl = `${companyInfo.url}${pathname}`;

  useEffect(() => {
    // Update document title
    document.title = finalTitle;

    // Helper to update or create meta tag
    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Helper to update or create link tag
    const updateLink = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Basic meta tags
    updateMeta('description', finalDescription);
    if (finalKeywords) {
      updateMeta('keywords', finalKeywords);
    }
    if (noindex) {
      updateMeta('robots', 'noindex, nofollow');
    } else {
      updateMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }

    // Canonical URL
    updateLink('canonical', canonicalUrl);

    // Open Graph tags
    updateMeta('og:title', finalTitle, true);
    updateMeta('og:description', finalDescription, true);
    updateMeta('og:type', type, true);
    updateMeta('og:url', canonicalUrl, true);
    updateMeta('og:image', finalImage, true);
    updateMeta('og:site_name', companyInfo.name, true);
    updateMeta('og:locale', 'en_US', true);

    // Twitter Card tags
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', finalTitle);
    updateMeta('twitter:description', finalDescription);
    updateMeta('twitter:image', finalImage);

    // Additional SEO meta tags
    updateMeta('author', companyInfo.name);
    updateMeta('geo.region', 'US-NC');
    updateMeta('geo.placename', companyInfo.address.city);
    updateMeta('geo.position', `${companyInfo.geo.lat};${companyInfo.geo.lng}`);
    updateMeta('ICBM', `${companyInfo.geo.lat}, ${companyInfo.geo.lng}`);

    // Schema.org JSON-LD
    const existingSchemas = document.querySelectorAll('script[type="application/ld+json"]');
    existingSchemas.forEach(script => script.remove());

    const schemas = [
      generateLocalBusinessSchema(),
      generateWebsiteSchema(),
      generateBreadcrumbSchema(pathname)
    ];

    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    // Cleanup function
    return () => {
      const schemaScripts = document.querySelectorAll('script[type="application/ld+json"]');
      schemaScripts.forEach(script => script.remove());
    };
  }, [pathname, finalTitle, finalDescription, finalKeywords, finalImage, canonicalUrl, type, noindex]);

  return null;
};

export default SEO;
export { companyInfo, pageSEO };
